/**
 * Blog topics カルーセル
 * - 以前: page-dots のハンドラで cols 未定義 → ReferenceError
 * - アクティブ: IntersectionObserver + スクロール時のオーバーラップ補助
 */
(function () {
  function goToPageLabel(i) {
    const tmpl = window.themeVariables?.strings?.goToPageForCarousel;
    return tmpl ? tmpl.replace(/\[NUM\]/g, String(i + 1)) : `Page ${i + 1}`;
  }

  function getScrollEl(inner) {
    if (!inner) {
      return null;
    }
    const wrap = inner.parentElement;
    const wrapDelta = wrap?.classList.contains("scroller") ? wrap.scrollWidth - wrap.clientWidth : 0;
    const innerDelta = inner.scrollWidth - inner.clientWidth;
    if (wrap?.classList.contains("scroller") && wrapDelta > 0 && wrapDelta >= innerDelta) {
      return wrap;
    }
    return inner;
  }

  function setActiveDot(dotsNav, idx) {
    dotsNav.querySelectorAll("button").forEach((btn, i) => {
      btn.setAttribute("aria-current", i === idx ? "true" : "false");
      btn.setAttribute("aria-selected", i === idx ? "true" : "false");
    });
  }

  function scrollToPageFirstCell(scrollEl, target) {
    if (!scrollEl || !target) {
      return;
    }
    const scrollRect = scrollEl.getBoundingClientRect();
    const cellRect = target.getBoundingClientRect();
    const nextLeft = cellRect.left - scrollRect.left + scrollEl.scrollLeft;
    scrollEl.scrollTo({ left: nextLeft, behavior: "smooth" });
  }

  function buildDotsIfNeeded(dotsNav, pages) {
    const existing = dotsNav.querySelectorAll("button").length;
    if (existing === pages) {
      return;
    }
    let html = "";
    for (let i = 0; i < pages; i++) {
      const label = goToPageLabel(i);
      html += `<button type="button" role="tab" class="dots-nav__item tap-area"${i === 0 ? ' aria-current="true" aria-selected="true"' : ' aria-selected="false"'}><span class="visually-hidden">${label}</span></button>`;
    }
    dotsNav.innerHTML = html;
  }

  /** スクロールポートと各セルの重なりでページ推定（IO の補助） */
  function updateActiveFromOverlap(list, inner, dotsNav, cols) {
    const scrollEl = getScrollEl(inner);
    const cells = list.querySelectorAll(".blog-topics__cell");
    const btns = dotsNav.querySelectorAll("button");
    if (!scrollEl || !cells.length || !btns.length) {
      return;
    }
    const pages = Math.max(1, Math.ceil(cells.length / cols));
    const sr = scrollEl.getBoundingClientRect();
    let bestI = 0;
    let bestRatio = -1;
    cells.forEach((cell, i) => {
      const r = cell.getBoundingClientRect();
      const overlap = Math.min(r.right, sr.right) - Math.max(r.left, sr.left);
      const ratio = overlap / Math.max(r.width, 1);
      if (ratio > bestRatio) {
        bestRatio = ratio;
        bestI = i;
      }
    });
    const pageIdx = Math.min(pages - 1, Math.floor(bestI / cols));
    setActiveDot(dotsNav, pageIdx);
  }

  function bind(list) {
    if (list.dataset.blogTopicsPagerInit) {
      return;
    }
    list.dataset.blogTopicsPagerInit = "true";

    const colsDesktop = parseInt(list.dataset.blogTopicsCols, 10) || 3;
    const mqPocket = window.matchMedia("(max-width: 740px)");
    const getCols = () => (mqPocket.matches ? 1 : colsDesktop);
    const inner = list.querySelector(".product-list__inner--scroller");
    const wrap = inner?.parentElement;
    const dotsNav = list.querySelector("[data-blog-topics-dots]");
    const pagerWrap = list.querySelector("[data-carousel-pager-wrap]");
    if (!inner || !dotsNav || !pagerWrap) {
      return;
    }

    let io = null;
    let ioAttached = { root: null, len: -1 };
    let navLockUntil = 0;

    function isNavLocked() {
      return Date.now() < navLockUntil;
    }

    function disconnectIo() {
      if (io) {
        io.disconnect();
        io = null;
      }
      ioAttached = { root: null, len: -1 };
    }

    function connectIoIfNeeded() {
      const scrollEl = getScrollEl(inner);
      const cells = list.querySelectorAll(".blog-topics__cell");
      if (!scrollEl || !cells.length) {
        disconnectIo();
        return;
      }
      if (io && ioAttached.root === scrollEl && ioAttached.len === cells.length) {
        return;
      }
      disconnectIo();
      ioAttached = { root: scrollEl, len: cells.length };

      const thresholds = [];
      for (let t = 0; t <= 1; t += 0.05) {
        thresholds.push(t);
      }

      io = new IntersectionObserver(
        (entries) => {
          if (isNavLocked()) {
            return;
          }
          const visible = entries.filter((e) => e.isIntersecting && e.intersectionRatio > 0.02);
          if (!visible.length) {
            return;
          }
          const best = visible.reduce((a, b) => (b.intersectionRatio > a.intersectionRatio ? b : a));
          const cellIndex = Array.prototype.indexOf.call(cells, best.target);
          if (cellIndex < 0) {
            return;
          }
          const cols = getCols();
          const pages = Math.max(1, Math.ceil(cells.length / cols));
          const pageIdx = Math.min(pages - 1, Math.floor(cellIndex / cols));
          if (dotsNav.querySelectorAll("button").length) {
            setActiveDot(dotsNav, pageIdx);
          }
        },
        { root: scrollEl, rootMargin: "0px", threshold: thresholds }
      );

      cells.forEach((cell) => io.observe(cell));
    }

    function runSync() {
      const cells = list.querySelectorAll(".blog-topics__cell");
      const scrollEl = getScrollEl(inner);
      const cols = getCols();
      const pages = Math.max(1, Math.ceil(cells.length / cols));

      if (pages <= 1 || !scrollEl) {
        pagerWrap.setAttribute("hidden", "");
        disconnectIo();
        return;
      }
      pagerWrap.removeAttribute("hidden");

      buildDotsIfNeeded(dotsNav, pages);
      connectIoIfNeeded();

      requestAnimationFrame(() => {
        if (!isNavLocked()) {
          updateActiveFromOverlap(list, inner, dotsNav, getCols());
        }
      });
    }

    const onScroll = () => {
      clearTimeout(list._blogTopicsScrollT);
      list._blogTopicsScrollT = setTimeout(() => {
        if (!isNavLocked()) {
          updateActiveFromOverlap(list, inner, dotsNav, getCols());
        }
      }, 24);
    };

    inner.addEventListener("scroll", onScroll, { passive: true });
    if (wrap?.classList.contains("scroller")) {
      wrap.addEventListener("scroll", onScroll, { passive: true });
    }

    dotsNav.addEventListener("click", (e) => {
      const btn = e.target.closest("button");
      if (!btn || !dotsNav.contains(btn)) {
        return;
      }
      const pageIndex = Array.prototype.indexOf.call(dotsNav.querySelectorAll("button"), btn);
      if (pageIndex < 0) {
        return;
      }
      navLockUntil = Date.now() + 700;
      setActiveDot(dotsNav, pageIndex);
      const cells = list.querySelectorAll(".blog-topics__cell");
      const cols = getCols();
      const target = cells[pageIndex * cols];
      const scrollEl = getScrollEl(inner);
      scrollToPageFirstCell(scrollEl, target);
      setTimeout(() => updateActiveFromOverlap(list, inner, dotsNav, getCols()), 760);
    });

    mqPocket.addEventListener("change", () => {
      disconnectIo();
      runSync();
    });

    const ro = new ResizeObserver(runSync);
    ro.observe(inner);
    if (wrap) {
      ro.observe(wrap);
    }

    requestAnimationFrame(runSync);
    setTimeout(runSync, 200);
    setTimeout(runSync, 600);
  }

  function initAll() {
    document.querySelectorAll("product-list.blog-topics__carousel").forEach(bind);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAll);
  } else {
    initAll();
  }

  document.addEventListener("shopify:section:load", (event) => {
    const root = event.target;
    if (!root?.querySelector?.(".blog-topics__carousel")) {
      return;
    }
    root.querySelectorAll("product-list.blog-topics__carousel").forEach((list) => {
      delete list.dataset.blogTopicsPagerInit;
      bind(list);
    });
  });
})();
